import React from "react";
import { Col, FormFeedback, Input, Label, Row } from "reactstrap";
import * as Yup from "yup";

import { fetchWrapper } from "@helpers/fetchwrapper";

import SelectAsyncPaginate from "@areas/shared/components/select/SelectAsyncPaginate";

import styles from "@areas/shared/components/personal/modals/styles/customModal.module.css";

const hollandFields = [
  { name: "hollandR", label: "R (реалистический)", api: "HollandR" },
  { name: "hollandI", label: "I (исследовательский)", api: "HollandI" },
  { name: "hollandA", label: "A (артистический)", api: "HollandA" },
  { name: "hollandS", label: "S (социальный)", api: "HollandS" },
  { name: "hollandE", label: "E (предпринимательский)", api: "HollandE" },
  { name: "hollandC", label: "C (конвенциональный)", api: "HollandC" },
];

const toHollandValue = (value) => {
  if (value == null || value === "") {
    return "";
  }
  return Number(value);
};

const initialValues = (relation) => {
  let okso = null;
  if (relation && relation.OksoCode) {
    okso = {
      value: relation.OksoCode,
      label: `${relation.OksoCode} ${relation.OksoTitle || ""}`.trim(),
      title: relation.OksoTitle || "",
    };
  }

  let okz = null;
  if (relation && (relation.OkzCode || relation.OkzTitle)) {
    okz = {
      value: relation.OkzCode || relation.OkzTitle,
      label: [relation.OkzCode, relation.OkzTitle].filter(Boolean).join(" "),
      code: relation.OkzCode || "",
      title: relation.OkzTitle || "",
    };
  }

  return {
    id: (relation && relation.Id) || null,
    sourceSpecialist: (relation && relation.SourceSpecialist) || "",
    okzCode: (relation && relation.OkzCode) || "",
    okzTitle: (relation && relation.OkzTitle) || "",
    okz,
    okso,
    specialistNeedsItemId:
      relation && relation.SpecialistNeedsItemId != null
        ? relation.SpecialistNeedsItemId
        : "",
    specialistNeedsItem: (relation && relation.SpecialistNeedsItem) || "",
    hollandR: toHollandValue(relation && relation.HollandR),
    hollandI: toHollandValue(relation && relation.HollandI),
    hollandA: toHollandValue(relation && relation.HollandA),
    hollandS: toHollandValue(relation && relation.HollandS),
    hollandE: toHollandValue(relation && relation.HollandE),
    hollandC: toHollandValue(relation && relation.HollandC),
  };
};

const hollandSchema = Yup.number()
  .nullable()
  .transform((value, originalValue) =>
    originalValue === "" || originalValue == null ? null : value
  )
  .min(0, "Значение от 0 до 1")
  .max(1, "Значение от 0 до 1");

const validationSchema = () => {
  return Yup.object({
    okzTitle: Yup.string().required("Значение обязательно"),
    okso: Yup.object().nullable().required("Выберите ОКСО"),
    hollandR: hollandSchema,
    hollandI: hollandSchema,
    hollandA: hollandSchema,
    hollandS: hollandSchema,
    hollandE: hollandSchema,
    hollandC: hollandSchema,
    specialistNeedsItemId: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue == null ? null : value
      )
      .integer("Должно быть целым числом")
      .min(1, "Некорректный идентификатор"),
  });
};

const appendHolland = (data, key, value) => {
  if (value !== "" && value != null) {
    data.append(key, value);
  }
};

const confirm = (values, successFunc) => {
  const data = new FormData();

  data.append("OksoCode", values.okso.value);
  data.append("OksoTitle", values.okso.title || values.okso.label);
  data.append("OkzTitle", values.okzTitle);
  data.append("SourceSpecialist", values.sourceSpecialist || "");
  data.append("OkzCode", values.okzCode || "");
  data.append("SpecialistNeedsItem", values.specialistNeedsItem || "");

  if (
    values.specialistNeedsItemId !== "" &&
    values.specialistNeedsItemId != null
  ) {
    data.append("SpecialistNeedsItemId", values.specialistNeedsItemId);
  }

  appendHolland(data, "HollandR", values.hollandR);
  appendHolland(data, "HollandI", values.hollandI);
  appendHolland(data, "HollandA", values.hollandA);
  appendHolland(data, "HollandS", values.hollandS);
  appendHolland(data, "HollandE", values.hollandE);
  appendHolland(data, "HollandC", values.hollandC);

  if (values.id) {
    data.append("Id", values.id);
    fetchWrapper
      .post(`/v1/api/app/UpdateCareerGuidanceRelation`, data, false)
      .then(() => successFunc());
  } else {
    fetchWrapper
      .post(`/v1/api/app/InsertCareerGuidanceRelation`, data, false)
      .then(() => successFunc());
  }
};

const loadOkzOptions = async (searchQuery, loadedOptions, { page }) => {
  const result = await fetchWrapper.get(
    `/v1/api/app/getOkzs/?text=${encodeURIComponent(
      searchQuery || ""
    )}&offset=${page * 10}&limit=10`
  );

  return {
    options: (result.data || []).map((item) => ({
      value: item.Code,
      label: `${item.Code} ${item.Name}`,
      code: item.Code,
      title: item.Name,
    })),
    hasMore: result.total > page * 10,
    additional: {
      page: searchQuery ? 1 : page + 1,
    },
  };
};

const loadOksoOptions = async (searchQuery, loadedOptions, { page }) => {
  const result = await fetchWrapper.get(
    `/v1/api/app/getOkso?text=${encodeURIComponent(
      searchQuery || ""
    )}&offset=${page * 10}&limit=10`
  );

  return {
    options: (result.data || []).map((item) => ({
      value: item.Code,
      label: `${item.Code} ${item.Title}`,
      title: item.Title,
    })),
    hasMore: result.total > page * 10,
    additional: {
      page: searchQuery ? 1 : page + 1,
    },
  };
};

function ModalBodyAdd({ validation }) {
  return (
    <Row className="g-2">
      <Input
        type="hidden"
        name="id"
        defaultValue={validation.values.id || null}
      />
      <Col sm={12}>
        <div>
          <Label htmlFor="careerSource-field" className={styles.label}>
            Исходная специальность
          </Label>
          <Input
            name="sourceSpecialist"
            id="careerSource-field"
            className={`form-control ${styles.input}`}
            placeholder="Название из потребностей предприятий"
            type="text"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.sourceSpecialist || ""}
          />
        </div>
      </Col>
      <Col sm={12}>
        <div>
          <Label htmlFor="careerOkzSelect-field" className={styles.label}>
            ОКЗ (справочник)
          </Label>
          <SelectAsyncPaginate
            name="okz"
            id="careerOkzSelect-field"
            loadFunc={loadOkzOptions}
            labelFunc={(e) => `${e.Code} ${e.Name}`}
            valueFunc={(e) => e.Code}
            placeholder="Выберите ОКЗ — заполнит код и название"
            value={validation.values.okz || null}
            onChangeValue={(option) => {
              validation.setFieldValue("okz", option);
              if (option) {
                validation.setFieldValue("okzCode", option.code || option.value);
                validation.setFieldValue(
                  "okzTitle",
                  option.title || option.label
                );
              }
            }}
            onBlurInput={() => validation.setFieldTouched("okz")}
          />
        </div>
      </Col>
      <Col sm={4}>
        <div>
          <Label htmlFor="careerOkzCode-field" className={styles.label}>
            Код ОКЗ
          </Label>
          <Input
            name="okzCode"
            id="careerOkzCode-field"
            className={`form-control ${styles.input}`}
            placeholder="Код ОКЗ"
            type="text"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.okzCode || ""}
          />
        </div>
      </Col>
      <Col sm={8}>
        <div>
          <Label htmlFor="careerOkzTitle-field" className={styles.label}>
            Название ОКЗ / профессии
          </Label>
          <Input
            name="okzTitle"
            id="careerOkzTitle-field"
            className={`form-control ${styles.input}`}
            placeholder="Обязательное поле"
            type="text"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.okzTitle || ""}
            invalid={
              validation.touched.okzTitle && validation.errors.okzTitle
                ? true
                : false
            }
          />
          {validation.touched.okzTitle && validation.errors.okzTitle ? (
            <FormFeedback type="invalid" className={styles.invalid}>
              {validation.errors.okzTitle}
            </FormFeedback>
          ) : null}
        </div>
      </Col>
      <Col sm={12}>
        <div>
          <Label htmlFor="careerOkso-field" className={styles.label}>
            ОКСО
          </Label>
          <SelectAsyncPaginate
            name="okso"
            id="careerOkso-field"
            loadFunc={loadOksoOptions}
            labelFunc={(e) => `${e.Code} ${e.Title}`}
            valueFunc={(e) => e.Code}
            placeholder="Выберите направление ОКСО"
            value={validation.values.okso || null}
            onChangeValue={(option) => {
              validation.setFieldValue("okso", option);
            }}
            onBlurInput={() => validation.setFieldTouched("okso")}
          />
          {validation.touched.okso && validation.errors.okso ? (
            <div className={`invalid-feedback d-block ${styles.invalid}`}>
              {validation.errors.okso}
            </div>
          ) : null}
        </div>
      </Col>
      <Col sm={4}>
        <div>
          <Label
            htmlFor="careerSpecialistNeedsId-field"
            className={styles.label}
          >
            ID потребности
          </Label>
          <Input
            name="specialistNeedsItemId"
            id="careerSpecialistNeedsId-field"
            className={`form-control ${styles.input}`}
            placeholder="SpecialistNeedsItemId"
            type="number"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.specialistNeedsItemId}
            invalid={
              validation.touched.specialistNeedsItemId &&
              validation.errors.specialistNeedsItemId
                ? true
                : false
            }
          />
          {validation.touched.specialistNeedsItemId &&
          validation.errors.specialistNeedsItemId ? (
            <FormFeedback type="invalid" className={styles.invalid}>
              {validation.errors.specialistNeedsItemId}
            </FormFeedback>
          ) : null}
        </div>
      </Col>
      <Col sm={8}>
        <div>
          <Label
            htmlFor="careerSpecialistNeedsItem-field"
            className={styles.label}
          >
            Текст потребности
          </Label>
          <Input
            name="specialistNeedsItem"
            id="careerSpecialistNeedsItem-field"
            className={`form-control ${styles.input}`}
            placeholder="SpecialistNeedsItem"
            type="text"
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.specialistNeedsItem || ""}
          />
        </div>
      </Col>
      {hollandFields.map((field) => (
        <Col sm={4} key={field.name}>
          <div>
            <Label htmlFor={`${field.name}-field`} className={styles.label}>
              {field.label}
            </Label>
            <Input
              name={field.name}
              id={`${field.name}-field`}
              className={`form-control ${styles.input}`}
              placeholder="0…1"
              type="number"
              step="0.01"
              min="0"
              max="1"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values[field.name]}
              invalid={
                validation.touched[field.name] &&
                validation.errors[field.name]
                  ? true
                  : false
              }
            />
            {validation.touched[field.name] &&
            validation.errors[field.name] ? (
              <FormFeedback type="invalid" className={styles.invalid}>
                {validation.errors[field.name]}
              </FormFeedback>
            ) : null}
          </div>
        </Col>
      ))}
    </Row>
  );
}

export { initialValues, validationSchema, confirm, ModalBodyAdd };
